import React, { useState } from 'react';
import { Search, SlidersHorizontal, ChevronRight, Heart, Activity, Droplets, Droplet, Shield, Syringe, Package2, TrendingUp } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { PRODUCTS, CATEGORIES, formatINR } from '@/data/mockData';

const SWISHX_LOGO = 'https://customer-assets.emergentagent.com/job_womens-health-app-16/artifacts/yvsedpej_swishX_logo1.png';

const CAT_ICON_MAP = {
  'Fertility':          { Icon: Heart,      color: '#E91E63', bg: '#FFF0F3' },
  "Women's Wellness":   { Icon: Activity,   color: '#9C27B0', bg: '#F3E5F5' },
  'Hormonal Support':   { Icon: Droplets,   color: '#3F51B5', bg: '#E8EAF6' },
  'Gynae Hematinics':   { Icon: Droplet,    color: '#F44336', bg: '#FFEBEE' },
  'Bone & Supportive':  { Icon: Shield,     color: '#388E3C', bg: '#E8F5E9' },
  'Injectable Range':   { Icon: Syringe,    color: '#FF5722', bg: '#FBE9E7' },
  'Miscellaneous':      { Icon: Package2,   color: '#607D8B', bg: '#ECEFF1' },
  'Top Movers':         { Icon: TrendingUp, color: '#FF9800', bg: '#FFF3E0' },
};

const GRADIENT_HEADING = {
  background: 'linear-gradient(to right, #E91E63, #FF5722)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

/* ─── Sub-components ─────────────────────────────────────── */

function SectionHeading({ title, onViewAll }) {
  return (
    <div className="flex justify-between items-center px-4 mt-5 mb-2.5">
      <span className="font-black text-[17px]" style={GRADIENT_HEADING}>{title}</span>
      {onViewAll && (
        <button onClick={onViewAll} className="flex items-center gap-1.5 tap-scale">
          <span className="text-gray-400 text-xs font-semibold">View All</span>
          <div className="w-5 h-5 rounded-full bg-[#FF5722] flex items-center justify-center">
            <ChevronRight size={12} color="white" />
          </div>
        </button>
      )}
    </div>
  );
}

function CategoryCard({ cat, onPress }) {
  const info = CAT_ICON_MAP[cat.name] || { Icon: Package2, color: '#607D8B', bg: '#ECEFF1' };
  const { Icon } = info;
  return (
    <button
      data-testid={`cat-card-${cat.id}`}
      onClick={() => onPress(cat)}
      className="rounded-xl p-2.5 flex flex-col justify-between tap-scale text-left"
      style={{ background: '#FAF6F4', minHeight: '72px' }}
    >
      <span className="font-bold text-[11px] text-gray-800 leading-tight">{cat.name}</span>
      <div className="flex justify-end mt-1">
        <div
          className="w-8 h-8 rounded flex items-center justify-center"
          style={{ background: info.bg, border: `1.5px solid ${info.color}` }}
        >
          <Icon size={16} color={info.color} />
        </div>
      </div>
    </button>
  );
}

function ProductCard({ product, onPress }) {
  const info = CAT_ICON_MAP[product.category] || { Icon: Package2, color: '#607D8B', bg: '#FFF3E0' };
  const { Icon } = info;
  return (
    <button
      data-testid={`product-card-${product.id}`}
      onClick={() => onPress && onPress(product)}
      className="flex-shrink-0 w-36 bg-white rounded-2xl overflow-hidden text-left tap-scale"
      style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.08)', border: '1px solid #F3F4F6' }}
    >
      <div className="h-28 flex items-center justify-center" style={{ background: info.bg }}>
        <Icon size={44} color={info.color} strokeWidth={1.5} />
      </div>
      <div className="p-2.5">
        <div className="font-bold text-[11px] text-gray-800 leading-tight mb-0.5 line-clamp-2">
          {product.name} ({product.pack})
        </div>
        <div className="text-[10px] text-gray-400 mb-2">M.R.P ₹{formatINR(product.mrp)}</div>
        <div
          className="w-full text-white text-[11px] font-bold py-1.5 rounded-lg text-center"
          style={{ background: '#FF5722' }}
        >
          Request
        </div>
      </div>
    </button>
  );
}

function HorizontalRow({ products, onProductPress }) {
  return (
    <div className="flex gap-3 overflow-x-auto pb-1 pl-4 pr-4 snap-scroll">
      {products.map(p => (
        <div key={p.id} className="snap-item">
          <ProductCard product={p} onPress={onProductPress} />
        </div>
      ))}
    </div>
  );
}

function FeaturedBanner() {
  return (
    <div
      className="mx-4 h-[100px] rounded-2xl flex items-center justify-between px-4 overflow-hidden relative"
      style={{ background: 'linear-gradient(135deg, #FF6B35 0%, #FF9A5C 60%, #FFD0A0 100%)' }}
    >
      <div className="z-10">
        <div className="text-white font-black text-xl leading-tight">Gynafol XT 60s</div>
        <div className="text-white/80 text-xs mt-0.5 mb-2">Extended prenatal support</div>
        <div
          className="inline-block text-[10px] font-black px-2.5 py-1 rounded-full"
          style={{ background: 'rgba(255,255,255,0.25)', color: 'white', border: '1px solid rgba(255,255,255,0.4)' }}
        >
          5%–20% OFF &gt;
        </div>
      </div>
      <div className="z-10 w-20 h-20 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
        <Activity size={40} color="white" strokeWidth={1.5} />
      </div>
      <div className="absolute -right-4 -top-4 w-24 h-24 rounded-full bg-white/10" />
    </div>
  );
}

function TopTrendingBanner({ products }) {
  const colors = ['#FF6B35', '#E91E63', '#9C27B0', '#3F51B5', '#2196F3', '#00BCD4'];
  return (
    <div className="flex gap-3 overflow-x-auto pb-1 pl-4 pr-4">
      {products.map((p, i) => {
        const info = CAT_ICON_MAP[p.category] || { Icon: Package2, color: 'white' };
        const { Icon } = info;
        const c = colors[i % colors.length];
        return (
          <div
            key={p.id}
            className="flex-shrink-0 w-40 h-24 rounded-2xl flex items-center justify-between px-3 overflow-hidden relative"
            style={{ background: `linear-gradient(135deg, ${c}DD, ${c}88)` }}
          >
            <div>
              <div className="text-white font-black text-xs leading-tight">{p.name}</div>
              <div className="text-white/70 text-[10px] mt-0.5">{p.pack}</div>
            </div>
            <Icon size={32} color="rgba(255,255,255,0.7)" strokeWidth={1.5} />
          </div>
        );
      })}
    </div>
  );
}

/* ─── Main Screen ────────────────────────────────────────── */

export default function ProductListScreen() {
  const { navigate } = useApp();
  const [searchQ, setSearchQ] = useState('');

  const frequentOrders  = PRODUCTS.filter(p => p.invoiceCount >= 7).sort((a, b) => b.invoiceCount - a.invoiceCount);
  const haematinics     = PRODUCTS.filter(p => p.category === 'Gynae Hematinics');
  const fertility       = PRODUCTS.filter(p => p.category === 'Fertility');
  const wellness        = PRODUCTS.filter(p => p.category === "Women's Wellness");
  const hormonal        = PRODUCTS.filter(p => p.category === 'Hormonal Support');
  const mostSelling     = PRODUCTS.filter(p => ['Frequently Invoiced', 'Active'].includes(p.status));
  const topTrending     = PRODUCTS.slice(0, 6);

  const searchResults = searchQ
    ? PRODUCTS.filter(p =>
        p.name.toLowerCase().includes(searchQ.toLowerCase()) ||
        p.segment.toLowerCase().includes(searchQ.toLowerCase())
      )
    : null;

  const goToCategory = (catName) => navigate('CATEGORY_PRODUCTS', { category: catName });

  return (
    <div className="pb-4" style={{ background: '#FAFAFA' }}>
      {/* White header */}
      <div className="bg-white px-4 pt-5 pb-3 border-b border-gray-100">
        <h1 className="text-center font-black tracking-widest text-gray-900 mb-3" style={{ fontSize: '15px', letterSpacing: '0.1em' }}>
          PRODUCT LIST
        </h1>
        <div className="flex gap-2">
          <div className="flex-1 flex items-center gap-2 rounded-xl px-3 py-2.5 border border-gray-200 bg-white">
            <Search size={16} color="#9CA3AF" />
            <input
              data-testid="product-search-input"
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              placeholder="Search for Femiron XT..."
              className="flex-1 text-sm outline-none bg-transparent text-gray-700 placeholder-gray-400"
            />
          </div>
          <button className="w-10 h-10 bg-white border border-gray-200 rounded-xl flex items-center justify-center tap-scale">
            <SlidersHorizontal size={16} color="#9CA3AF" />
          </button>
        </div>
      </div>

      {searchResults ? (
        /* ── Search results ── */
        <div className="px-4 pt-4">
          <div className="text-xs text-gray-500 mb-3">{searchResults.length} results for "{searchQ}"</div>
          <div className="grid grid-cols-2 gap-3">
            {searchResults.map(p => (
              <ProductCard key={p.id} product={p} onPress={() => goToCategory(p.category)} />
            ))}
          </div>
        </div>
      ) : (
        /* ── Main content ── */
        <>
          <div className="pt-4">
            <FeaturedBanner />
          </div>

          {/* Categories */}
          <div className="px-4 mt-5">
            <div className="flex justify-between items-center mb-3">
              <span className="font-black text-[17px]" style={GRADIENT_HEADING}>Categories</span>
              <button className="flex items-center gap-1.5">
                <span className="text-gray-400 text-xs font-semibold">View All</span>
                <div className="w-5 h-5 rounded-full bg-[#FF5722] flex items-center justify-center">
                  <ChevronRight size={12} color="white" />
                </div>
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {CATEGORIES.map(cat => (
                <CategoryCard key={cat.id} cat={cat} onPress={c => goToCategory(c.name)} />
              ))}
            </div>
          </div>

          {/* Frequent Orders */}
          <SectionHeading title="Frequent Orders" onViewAll={() => goToCategory('Frequent Orders')} />
          <HorizontalRow products={frequentOrders} onProductPress={p => goToCategory(p.category)} />

          {/* Gynae Hematinics */}
          <SectionHeading title="Gynae Hematinics" onViewAll={() => goToCategory('Gynae Hematinics')} />
          <HorizontalRow products={haematinics} onProductPress={p => goToCategory(p.category)} />

          {/* Fertility Range */}
          <SectionHeading title="Fertility Range" onViewAll={() => goToCategory('Fertility')} />
          <HorizontalRow products={fertility} onProductPress={p => goToCategory(p.category)} />

          {/* Women's Wellness */}
          <SectionHeading title="Women's Wellness" onViewAll={() => goToCategory("Women's Wellness")} />
          <HorizontalRow products={wellness} onProductPress={p => goToCategory(p.category)} />

          {/* Top Trending */}
          <div className="flex justify-between items-center px-4 mt-5 mb-2.5">
            <span className="font-black text-[17px]" style={GRADIENT_HEADING}>Top Trending</span>
          </div>
          <TopTrendingBanner products={topTrending} />

          {/* Most Selling */}
          <SectionHeading title="Most Selling" onViewAll={() => goToCategory('All')} />
          <HorizontalRow products={mostSelling.slice(0, 6)} onProductPress={p => goToCategory(p.category)} />

          {/* Hormonal Support */}
          <SectionHeading title="Hormonal Support" onViewAll={() => goToCategory('Hormonal Support')} />
          <HorizontalRow products={hormonal} onProductPress={p => goToCategory(p.category)} />

          {/* SwishX footer */}
          <div className="flex justify-center items-center mt-8 mb-2 gap-2">
            <span className="text-gray-400 text-xs">Powered by</span>
            <img
              src={SWISHX_LOGO}
              alt="SwishX"
              className="h-5 w-auto"
              style={{ filter: 'brightness(0) saturate(0) opacity(0.35)' }}
            />
          </div>
        </>
      )}
    </div>
  );
}
